"use client";

import { useEffect, useRef, useState } from "react";
import { RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { fetchAppVersion } from "@/lib/browser/app-version";
import { prepareAppRefresh } from "@/lib/browser/app-refresh";

const CHECK_INTERVAL = 5 * 60_000;
const SNOOZE_DURATION = 30 * 60_000;

export function AppUpdatePrompt() {
  const currentVersion = process.env.NEXT_PUBLIC_APP_VERSION;
  const [availableVersion, setAvailableVersion] = useState<string | null>(null);
  const [online, setOnline] = useState(true);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const snoozedUntil = useRef(0);
  const mounted = useRef(false);

  useEffect(() => {
    mounted.current = true;
    let request: AbortController | null = null;
    async function check() {
      setOnline(navigator.onLine);
      if (
        !currentVersion ||
        !navigator.onLine ||
        document.visibilityState === "hidden" ||
        request
      )
        return;
      const controller = new AbortController();
      request = controller;
      const timeout = window.setTimeout(() => controller.abort(), 10_000);
      try {
        const version = await fetchAppVersion(controller.signal);
        if (!mounted.current || controller.signal.aborted) return;
        setAvailableVersion(
          version !== currentVersion && Date.now() >= snoozedUntil.current
            ? version
            : null,
        );
      } catch {
        // A weak connection or an unavailable server is not an app update.
      } finally {
        window.clearTimeout(timeout);
        request = null;
      }
    }
    void check();
    const interval = window.setInterval(() => void check(), CHECK_INTERVAL);
    window.addEventListener("focus", check);
    window.addEventListener("pageshow", check);
    window.addEventListener("online", check);
    window.addEventListener("offline", check);
    document.addEventListener("visibilitychange", check);
    return () => {
      mounted.current = false;
      request?.abort();
      window.clearInterval(interval);
      window.removeEventListener("focus", check);
      window.removeEventListener("pageshow", check);
      window.removeEventListener("online", check);
      window.removeEventListener("offline", check);
      document.removeEventListener("visibilitychange", check);
    };
  }, [currentVersion]);

  async function refresh() {
    if (busy) return;
    setBusy(true);
    setError(null);
    const controller = new AbortController();
    const timeout = window.setTimeout(() => controller.abort(), 10_000);
    try {
      // Recheck reachability: navigator.onLine alone does not prove connectivity.
      await fetchAppVersion(controller.signal);
      await prepareAppRefresh();
      if (mounted.current) window.location.reload();
    } catch (cause) {
      if (mounted.current)
        setError(
          cause instanceof Error
            ? cause.message
            : "Could not prepare the update. Please try again.",
        );
    } finally {
      window.clearTimeout(timeout);
      if (mounted.current) setBusy(false);
    }
  }

  if (!availableVersion || !online) return null;

  return (
    <>
      <section
        aria-label="App update"
        className="bg-card text-card-foreground fixed right-3 bottom-[max(1rem,env(safe-area-inset-bottom))] left-3 z-40 rounded-xl border p-4 shadow-lg sm:right-5 sm:left-auto sm:w-96"
      >
        <div role="status" className="flex items-start gap-3">
          <RefreshCw
            aria-hidden="true"
            className="text-primary mt-0.5 size-5 shrink-0"
          />
          <div>
            <p className="text-sm font-semibold">New version available</p>
            <p className="text-muted-foreground mt-1 text-sm">
              Refresh when you&apos;re ready to get the latest updates.
            </p>
          </div>
        </div>
        <div className="mt-3 flex justify-end gap-2">
          <Button
            type="button"
            variant="ghost"
            onClick={() => {
              snoozedUntil.current = Date.now() + SNOOZE_DURATION;
              setAvailableVersion(null);
            }}
          >
            Later
          </Button>
          <Button
            type="button"
            onClick={() => {
              setError(null);
              setConfirmOpen(true);
            }}
          >
            Refresh now
          </Button>
        </div>
      </section>
      <AlertDialog
        open={confirmOpen}
        onOpenChange={(open) => {
          if (!busy) setConfirmOpen(open);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Ready to refresh?</AlertDialogTitle>
            <AlertDialogDescription>
              Save your work before refreshing. Unsaved changes may be lost.
              Field work already saved on this device will stay here.
            </AlertDialogDescription>
          </AlertDialogHeader>
          {error && (
            <p role="alert" className="text-destructive text-sm">
              {error}
            </p>
          )}
          <AlertDialogFooter>
            <AlertDialogCancel disabled={busy}>Keep working</AlertDialogCancel>
            <AlertDialogAction disabled={busy} onClick={() => void refresh()}>
              {busy ? "Preparing update…" : "Refresh app"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
